import { useState, useEffect } from "react";
import { createWatchlistItem, deleteWatchlistItem } from "../api/api";
import { useWatchlistData } from "../context/WatchlistContext";

const WatchlistModal = ({ setWatchlistModalOpen }) => {
  const { items: watchlist, refresh: refreshWatchlist } = useWatchlistData();
  const [newInstrument, setNewInstrument] = useState("");
  const [newExchange, setNewExchange] = useState(""); // Added state for exchange
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch watchlist from API on mount
  const fetchItems = async ({ force = false } = {}) => {
    setIsLoading(true);
      setError(null);
      console.log("WatchlistModal: Starting fetchItems...");
      try {
      const itemsArray = await refreshWatchlist({ force });
      console.log("WatchlistModal: Watchlist provider returned length:", Array.isArray(itemsArray) ? itemsArray.length : 0);
    } catch (err) {
      console.error("WatchlistModal: Error in fetchItems:", err); 
      setError(err.detail || "Failed to fetch watchlist items. Please try again.");
    } finally {
      setIsLoading(false);
      console.log("WatchlistModal: Finished fetchItems.");
    }
  };

  useEffect(() => {
    fetchItems();
  }, []); // Provider dedupes this with the app-level initial load.

  // Function to add an instrument
  const handleAddInstrument = async () => {
    if (newInstrument && newExchange) {
      setIsLoading(true);
      setError(null);
      try {
        // Check for duplicates locally before API call (optional, API handles it too)
        const exists = watchlist.some(
          item => item.instrument === newInstrument && item.exchange === newExchange
        );
        if (exists) {
          setError("This instrument/exchange combination already exists in your watchlist.");
          setIsLoading(false);
          return;
        }

        await createWatchlistItem({ instrument: newInstrument, exchange: newExchange });
        setNewInstrument("");
        setNewExchange("");
        await fetchItems({ force: true }); // Refresh list
      } catch (err) {
        console.error("Failed to add watchlist item:", err);
        setError(err.detail || "Failed to add item. Check if it already exists or try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      setError("Both instrument and exchange are required.");
    }
  };

  // Function to remove an instrument
  const handleRemoveInstrument = async (itemId) => {
    setIsLoading(true);
    setError(null);
    try {
      await deleteWatchlistItem(itemId);
      await fetchItems({ force: true }); // Refresh list
    } catch (err) {
      console.error("Failed to remove watchlist item:", err);
      setError(err.detail || "Failed to remove item. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md mx-4">
        <h3 className="text-xl font-semibold text-[var(--primary-color)] mb-4">Manage Watchlist</h3>

        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        {isLoading && <p className="text-blue-500 text-sm mb-3">Loading...</p>}

        {/* Watchlist Items */}
        <div className="max-h-60 overflow-y-auto mb-4">
          <ul>
            {watchlist && watchlist.length > 0 ? (
              watchlist.map((item) => (
                <li key={item.id} className="flex justify-between items-center bg-[var(--secondary-color)] dark:bg-gray-700 p-3 rounded-lg mb-2 shadow">
                  <span className="text-[var(--text-color)] dark:text-gray-200">
                    {item.instrument} ({item.exchange})
                    {item.is_global && <span className="text-xs text-blue-500 ml-2">(Global)</span>}
                  </span>
                  <button
                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-600 text-xl"
                    onClick={() => handleRemoveInstrument(item.id)}
                    disabled={isLoading}
                  >
                    &times;
                  </button>
                </li>
              ))
            ) : (
              !isLoading && <p className="text-[var(--text-color)] dark:text-gray-400 text-center py-4">No instruments in watchlist.</p>
            )}
          </ul>
        </div>

        {/* Add Instrument Inputs */}
        <div className="space-y-3">
          <input
            type="text"
            className="w-full p-2 border rounded-lg bg-[var(--secondary-color)] dark:bg-gray-700 text-[var(--text-color)] dark:text-gray-200 dark:border-gray-600"
            placeholder="Instrument (e.g., AAPL, EURUSD)"
            value={newInstrument}
            onChange={(e) => setNewInstrument(e.target.value)}
            disabled={isLoading}
          />
          <input
            type="text"
            className="w-full p-2 border rounded-lg bg-[var(--secondary-color)] dark:bg-gray-700 text-[var(--text-color)] dark:text-gray-200 dark:border-gray-600"
            placeholder="Exchange (e.g., NASDAQ, FXCM)"
            value={newExchange}
            onChange={(e) => setNewExchange(e.target.value)}
            disabled={isLoading}
          />
          <button
            className="btn btn-primary w-full"
            onClick={handleAddInstrument}
            disabled={isLoading || !newInstrument || !newExchange}
          >
            {isLoading ? "Adding..." : "Add to Watchlist"}
          </button>
        </div>

        {/* Close Button */}
        <button
          className="btn btn-secondary mt-3 w-full"
          onClick={() => setWatchlistModalOpen(false)}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default WatchlistModal;
